import { FC, useEffect, useMemo, useState } from 'react';

import { DbtResourceIcon, ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { Breadcrumbs, CellContext, Link } from '@dbt-labs/sourdough';

import { Page } from '@/components/Page';
import { PageHeading } from '@/components/PageHeading';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAllAppliedViewBy } from '@/hooks/discovery/allAppliedViewBy';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams, useValidatedParams } from '@/hooks/params';
import { useProjectBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { PageInfo } from '@/reducers/usePaginatedQuery';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type SchemaTableData = {
  uniqueId: string;
  name: string;
  resourceType: ResourceTypeExplorer;
  database: string | null;
  schema: string | null;
  tags: string[] | null;
};

type SchemaResource = {
  uniqueId: string;
  tags: string[] | null;
  database: string | null;
  schema: string | null;
};

const getTableData = ({
  uniqueId: rawUniqueId,
  database,
  schema,
  tags,
}: SchemaResource): SchemaTableData => {
  const uniqueId = parseUniqueId(rawUniqueId);
  return {
    uniqueId: uniqueId.raw,
    name: uniqueId.remainder,
    resourceType: uniqueId.resourceType,
    database,
    schema,
    tags,
  };
};

export const SchemaFilterPage: FC = () => {
  const { databaseName, schemaName } = useValidatedParams();

  const breadcrumbs = useProjectBreadcrumbs({
    additionalItems: [
      { text: databaseName ?? '', skeleton: databaseName === undefined },
      { text: schemaName ?? '', skeleton: schemaName === undefined },
    ],
  });

  const { accountId, environmentId, projectId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const { environmentType: environmentName } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');

  const [lineageQuery] = useAllAppliedViewBy({ environmentId, userId });
  const pageSize = INITIAL_PAGE_SIZE;
  const [shownResults, setShownResults] = useState(INITIAL_PAGE_SIZE);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    endCursor: null,
    hasNextPage: false,
    totalCount: null,
  });
  const isInitialLoad = lineageQuery === undefined || lineageQuery.fetching;

  const schemaLineage = useMemo(
    () =>
      (lineageQuery.data?.environment.applied?.lineage ?? []).filter((node) => {
        const database = 'database' in node ? node.database : null;
        const schema = 'schema' in node ? node.schema : null;
        return (
          database?.toLowerCase() === databaseName?.toLowerCase() &&
          schema?.toLowerCase() === schemaName?.toLowerCase()
        );
      }),
    [lineageQuery.data?.environment.applied?.lineage, databaseName, schemaName],
  );

  useEffect(() => {
    const len = schemaLineage.length;
    const totalCount = len == null ? null : len;
    setPageInfo({
      endCursor: null,
      hasNextPage: (totalCount || 0) > shownResults,
      totalCount,
    });
  }, [schemaLineage.length, shownResults]);

  useEffect(() => {
    setShownResults(pageSize);
  }, [schemaName, pageSize]);

  const data = useMemo(() => {
    return schemaLineage.map(getTableData).slice(0, shownResults);
  }, [schemaLineage, shownResults]);

  if (!databaseName || !schemaName || !accountId) return null;

  return (
    <Page title={schemaName} pageType="databaseDetails">
      <Breadcrumbs className="mb-3" breadcrumbs={breadcrumbs} />
      <PageHeading className="mb-10">{schemaName}</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="resources"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorFn: (data) => ({
              name: data.name,
              resourceType: data.resourceType as ResourceTypeExplorer,
              uniqueId: data.uniqueId,
            }),
            id: 'name',
            cell: ({ getValue }: CellContext<SchemaTableData, any>) => (
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  projectId,
                  environmentType: environmentName,
                  dbtUniqueId: getValue().uniqueId,
                })}
              >
                {getValue().name}
              </Link>
            ),
          },
          {
            header: 'Resource type',
            accessorKey: 'resourceType',
            maxSize: 200,
            cell: ({ getValue }) => (
              <span className="flex items-center space-x-1 whitespace-nowrap">
                <DbtResourceIcon className="inline-block" resource={getValue()} />
                <span className="inline-block align-middle">{getValue()}</span>
              </span>
            ),
          },
          {
            header: 'Tags',
            accessorKey: 'tags',
            accessorFn: (data) => data.tags?.join(', '),
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        // Pagination is faked clientside, there are no more results to load
        isLoadingMore={false}
        pageSize={pageSize}
        fetchMore={() => setShownResults((prev) => prev + pageSize)}
      />
    </Page>
  );
};
